import type { ProgramCodeBook, EditProgramCodeBookRequest, ProgramMinisterDetailDto } from "../../models/Program";
import { ProgramType } from "../../models/Program";

export type ProgramApiRequestDto = {
  name: string;
  programTypeEMaticaId: number | null;
  programTypeId: number | undefined;
  qualificationTypeId: number | null;
  hkoLevelId: number | null;
  educationSectorId: number | null;
  educationSubSectorId: number | null;
  qualificationId: number | null;
  professionFieldId: number | null;
  professionId: number | null;
  languageId: number | null;
  dateTo: string | null;
  programWithMinisterResolution: boolean;
  ministerDetail?: ProgramMinisterDetailDto;
};

const normalizeProgramDates = (item: ProgramCodeBook) => {
  if (item.dateTo) {
    item.dateTo = new Date(item.dateTo);
  }
};

export const normalizeProgram = (item: ProgramCodeBook) => {
  normalizeProgramDates(item);

  if (!item.ministerDetail) {
    return;
  }

  const detail = item.ministerDetail;
  item.hkoLevelName = detail.hkoLevelText;
  item.educationSectorName = detail.educationSectorText;
  item.educationSubSectorName = detail.educationSubSectorText;
};

const buildMinisterDetail = (
  detail: EditProgramCodeBookRequest["ministerDetail"],
): ProgramMinisterDetailDto => ({
  hkoLevelText: detail?.hkoLevelText ?? "",
  educationSectorText: detail?.educationSectorText ?? "",
  educationSubSectorText: detail?.educationSubSectorText ?? "",
  guidedLearningDuration: detail?.guidedLearningDuration ?? 0,
  independentLearningDuration: detail?.independentLearningDuration ?? 0,
  workLearningDuration: detail?.workLearningDuration ?? 0,
  formOfTeachingIds: detail?.formOfTeachingIds ?? [],
  otherFormsOfTeaching: detail?.otherFormsOfTeaching ?? [],
  enrollmentConditionIds: detail?.enrollmentConditionIds ?? [],
  otherEnrollmentConditions: detail?.otherEnrollmentConditions ?? [],
  knowledgeCheckIds: detail?.knowledgeCheckIds ?? [],
  otherKnowledgeChecks: detail?.otherKnowledgeChecks ?? [],
  competences: (detail?.competences ?? []).map((c) => c.trim()).filter(Boolean),
  modules: detail?.modules ?? [],
});

const toNullableId = (value: number | null | undefined): number | null => {
  if (value == null || value <= 0) {
    return null;
  }
  return value;
};

export const toProgramApiRequest = (request: EditProgramCodeBookRequest): ProgramApiRequestDto => {
  const programWithMinisterResolution = request.programWithMinisterResolution ?? false;
  const ministerDetail = programWithMinisterResolution ? buildMinisterDetail(request.ministerDetail) : undefined;

  return {
    name: request.name,
    programTypeEMaticaId: toNullableId(request.programTypeEMaticaId),
    programTypeId: programWithMinisterResolution ? ProgramType.ProgramWithMinisterResolution : request.programTypeId,
    qualificationTypeId: toNullableId(request.qualificationTypeId),
    hkoLevelId: request.hkoLevelId ?? null,
    educationSectorId: programWithMinisterResolution ? null : request.educationSectorId ?? null,
    educationSubSectorId: programWithMinisterResolution ? null : request.educationSubSectorId ?? null,
    qualificationId: toNullableId(request.qualificationId),
    professionFieldId: toNullableId(request.professionFieldId),
    professionId: toNullableId(request.professionId),
    languageId: toNullableId(request.languageId),
    dateTo: request.dateTo ? request.dateTo.toISOString() : null,
    programWithMinisterResolution: programWithMinisterResolution,
    ministerDetail: ministerDetail,
  };
};

